import { useCallback, useEffect, useState } from "react";

import { useT, type Translate } from "../../../shared/i18n";
import { Badge, Button, Skeleton } from "../../../shared/ui";
import { AdminTabs } from "../AdminTabs";
import { adminHref, isUserTab, USER_TABS, type UserTabId } from "../adminRoute";
import { ErrorRetry } from "../ErrorRetry";
import type { TraceApi } from "../logs";
import { OperatorDenied } from "../operatorHttp";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { ActivitySection } from "./ActivitySection";
import { BillingSection } from "./BillingSection";
import { shortUserId, userLabel, type UserLabel } from "./format";
import { IdentitiesSection } from "./IdentitiesSection";
import { ProfileSection } from "./ProfileSection";
import { StorageSection } from "./StorageSection";
import { UserLogsTab } from "./UserLogsTab";
import type { UserDetailData, UsersApi } from "./usersApi";

type State =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "denied" }
  | { kind: "ready"; user: UserDetailData };

function tabLabel(t: Translate, id: UserTabId): string {
  switch (id) {
    case "profile":
      return t("admin.users.tab.profile");
    case "activity":
      return t("admin.users.tab.activity");
    case "storage":
      return t("admin.users.tab.storage");
    case "billing":
      return t("admin.users.tab.billing");
    case "identities":
      return t("admin.users.tab.identities");
    case "logs":
      return t("admin.users.tab.logs");
  }
}

/** Display name and Telegram id from the profile/identities, when permitted. */
function headerLabel(user: UserDetailData): UserLabel {
  const telegram = user.external_identities.find(
    (identity) => identity.provider === "telegram",
  );
  const named = user.external_identities.find(
    (identity) => identity.display_name,
  );
  return userLabel({
    user_id: user.user_id,
    display_name: user.profile.display_name ?? named?.display_name ?? null,
    telegram_id: telegram?.provider_subject ?? null,
  });
}

/** One user as a routed page: header plus Profile/Activity/.../Logs tabs. */
export function UserPage({
  api,
  traceApi,
  userId,
  tab,
  wide,
  onNavigate,
  onBack,
}: {
  api: UsersApi;
  traceApi: TraceApi;
  userId: string;
  tab: UserTabId;
  wide: boolean;
  onNavigate: (href: string) => void;
  /** Single-pane back to the list; omitted when the list is beside the page. */
  onBack?: () => void;
}) {
  const t = useT();
  const reportExpired = useReportSessionExpired();
  const [state, setState] = useState<State>({ kind: "loading" });

  const run = useCallback(() => {
    api
      .detail(userId)
      .then((user) => {
        setState({ kind: "ready", user });
      })
      .catch((error: unknown) => {
        if (isSessionExpired(error)) reportExpired();
        setState({
          kind: error instanceof OperatorDenied ? "denied" : "failed",
        });
      });
  }, [api, userId, reportExpired]);

  useEffect(() => {
    run();
  }, [run]);

  const label: UserLabel =
    state.kind === "ready"
      ? headerLabel(state.user)
      : { primary: shortUserId(userId), secondary: null };

  return (
    <section aria-label={label.primary} className="flex min-w-0 flex-col gap-3">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-raised p-4 shadow-card">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            {t("admin.users.back")}
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-title text-fg">{label.primary}</h2>
          <p className="flex flex-wrap gap-x-3 text-caption text-fg-muted">
            {label.secondary !== null && (
              <span>
                {t("admin.users.header.telegram")}:{" "}
                <span className="font-mono">{label.secondary}</span>
              </span>
            )}
            <span>
              {t("admin.users.header.id")}:{" "}
              <span className="font-mono">{userId}</span>
            </span>
          </p>
        </div>
        {state.kind === "ready" && (
          <Badge
            tone={
              state.user.profile.status === "active" ? "success" : "warning"
            }
          >
            {state.user.profile.status}
          </Badge>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            onNavigate(adminHref({ section: "logs", logsUserId: userId }));
          }}
        >
          {t("admin.users.openInLogs")}
        </Button>
      </header>
      {state.kind === "loading" && (
        <div aria-busy="true">
          <Skeleton height={96} />
          <span className="sr-only">{t("admin.users.detail.loading")}</span>
        </div>
      )}
      {state.kind === "failed" && (
        <ErrorRetry
          message={t("admin.users.detail.failed")}
          onRetry={() => {
            setState({ kind: "loading" });
            run();
          }}
        />
      )}
      {state.kind === "denied" && (
        <PermissionDenied message={t("admin.denied.users")} />
      )}
      {state.kind === "ready" && (
        <AdminTabs
          label={t("admin.users.tabs.label")}
          value={tab}
          onValueChange={(next) => {
            if (isUserTab(next)) {
              onNavigate(
                adminHref({ section: "users", userId, userTab: next }),
              );
            }
          }}
          items={USER_TABS.map((id) => ({
            value: id,
            label: tabLabel(t, id),
            content: userTabContent(id, state.user, traceApi, wide, (cid) => {
              onNavigate(adminHref({ section: "logs", correlationId: cid }));
            }),
          }))}
        />
      )}
    </section>
  );
}

function userTabContent(
  id: UserTabId,
  user: UserDetailData,
  traceApi: TraceApi,
  wide: boolean,
  openTrace: (correlationId: string) => void,
) {
  switch (id) {
    case "profile":
      return <ProfileSection user={user} />;
    case "activity":
      return (
        <ActivitySection activity={user.activity} onOpenLogs={openTrace} />
      );
    case "storage":
      return <StorageSection storage={user.storage} />;
    case "billing":
      return (
        <BillingSection
          billing={user.billing}
          redacted={user.billing_redacted === true}
        />
      );
    case "identities":
      return (
        <IdentitiesSection
          identities={user.external_identities}
          redacted={user.pii_redacted === true}
        />
      );
    case "logs":
      return <UserLogsTab api={traceApi} userId={user.user_id} wide={wide} />;
  }
}
